import { useEffect, useState } from "react";
import type { KaiAgentState } from "../Components/KaiAgentStatus/KaiAgentStatus";
import { useTicketStore } from "../Stores/Ticket";
import {
  DONE_HOLD_MS,
  TOOL_BLIP_MS,
  deriveKaiAgentState,
} from "./KaiAgentState";

/**
 * The Kai avatar's state for one message row. `live` rows (the typing
 * placeholder, the streaming bubble, the latest answer) follow the store;
 * history rows stay idle and only blink. Narrow selectors keep the row from
 * re-rendering on every streamed token more than it already does.
 */
export const useKaiAgentState = ({
  live,
  typing,
  settled = false,
}: {
  live: boolean;
  typing: boolean;
  /** A settled answer row: attention / paused / done only, no streaming states. */
  settled?: boolean;
}): KaiAgentState => {
  const botWrittingText = useTicketStore((s) => s.botWrittingText);
  const botWrittingIsThinking = useTicketStore((s) => s.botWrittingIsThinking);
  const botWrittingState = useTicketStore((s) => s.botWrittingState);
  const botToolAt = useTicketStore((s) => s.botToolAt);
  const botTurnEndedAt = useTicketStore((s) => s.botTurnEndedAt);
  const currentBotActionType = useTicketStore(
    (s) => s.currentBotAction?.action?.type as string | undefined,
  );
  const [now, setNow] = useState(() => Date.now());

  // The tool blip and the done hold are windows: re-render once when the
  // open one closes, so the tile settles without anyone polling.
  useEffect(() => {
    if (!live) return undefined;
    const current = Date.now();
    setNow(current);
    const deadlines = [
      botToolAt ? botToolAt + TOOL_BLIP_MS : 0,
      botTurnEndedAt ? botTurnEndedAt + DONE_HOLD_MS : 0,
    ].filter((deadline) => deadline > current);
    if (deadlines.length === 0) return undefined;
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(...deadlines) - current + 20,
    );
    return () => window.clearTimeout(timer);
  }, [live, botToolAt, botTurnEndedAt]);

  if (!live) return "idle";
  return deriveKaiAgentState(
    {
      botWrittingText,
      botWrittingIsThinking,
      botWrittingState,
      botToolAt,
      botTurnEndedAt,
      typing,
      settled,
      currentBotActionType,
    },
    now,
  );
};
